<template>
    <div class="tile-content tile-content-secondary">
        <p>
            Schedule reminders when {{humanizedRelatedEvent}}. If there is already an existing {{relatedEventName}}
            reminder for this {{relatedEntityName}}, this will overwrite it. To send emails, create your notifications
            on the
            <router-link
                v-if="$auth.rulesets.read.isAllowed"
                :to="{name: 'view-notifications', params: {eventType: relatedReminderEvent}}">
                {{relatedReminderEvent | unKebabCase}}
            </router-link>
            <span v-else>{{relatedReminderEvent | unKebabCase}}</span>
            event.
        </p>
        <div class="divider divider-tile-content"></div>
        <h3 class="field-label stack-s">Schedule Attempts</h3>
        <p class="stack-m">First attempt is going to be relative to the target event and next attempts will occur after
            previous attempt</p>
        <table class="table">
            <colgroup>
                <col style="width:50px;">
                <col width="40%">
                <col width="20%">
                <col width="40%">
                <col style="width:50px;">
            </colgroup>
            <thead>
            <tr>
                <th>Order</th>
                <th>Schedule Attempt</th>
                <th></th>
                <th>Relative to</th>
                <th></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(instruction, index) in action.schedule.instructions" :key="index">
                <td class="align-center">
                    {{ index + 1 }}
                </td>
                <td>
                    <define-schedule-view
                        :schedule-instruction="instruction"
                        common-schedule
                        :validate="$v.action.schedule.instructions[index]"
                        @update="data => updateInstruction(index, data)"/>
                </td>
                <td>
                    <v-popper-select
                        v-if="index === 0"
                        class="inline-xs"
                        :options="chronologyOptions"
                        :value="action.schedule.chronology"
                        :validate="$v.action.schedule.chronology"
                        @input="value => updateChronology(index, value)"/>
                </td>
                <td>
                    <div v-if="index === 0">
                        <div class="field" v-if="canHaveRole">
                            <v-radio
                                v-for="(title, id) in selectableRoles"
                                class="stack-s"
                                :key="id"
                                :group="groupName"
                                :label="title"
                                :value="id"
                                :model="role"
                                @update="updateRole"/>
                        </div>
                        <div v-else>{{relatedEvent}}</div>
                    </div>
                    <div v-else>{{$filters.ordinal(index + 1)}} attempt</div>
                </td>
                <td>
                    <v-icon-button type="basic"
                                   @click="deleteInstruction(index)"
                                   v-if="index > 0">
                        <v-icon icon="trash"/>
                    </v-icon-button>
                </td>
            </tr>
            </tbody>
        </table>
        <div>
            <div class="divider no-margin-top"></div>
            <r-button type="link" @click="addInstruction">Add Attempt</r-button>
        </div>
    </div>
</template>

<script>
    import shortid from 'shortid';
    import {
        vIcon,
        vIconButton,
        vRadio,
        vPopperSelect,
    } from '@/components/form';
    import {required} from 'vuelidate/lib/validators';
    import ReminderAction from '@/modules/rules-engine/actions/reminder-action';
    import {ScheduleModel} from '@/common/models/rules/actions/schedule-reminder-action-model';
    import ScheduleInstructionModel from '@/common/models/rules/schedule-instruction-model';
    import DefineScheduleView from '@/modules/rules-engine/components/actions/schedule-payment/define-schedule-view';
    import getScheduleValidation
        from '@/modules/rules-engine/components/actions/schedule-payment/get-schedule-validation';

    export default {
        name: 'schedule-reminder',
        extends: ReminderAction,
        components: {
            vRadio,
            vIcon,
            vIconButton,
            vPopperSelect,
            DefineScheduleView,
        },
        computed: {
            chronologyOptions() {
                return Object.values(ScheduleModel.Chronologies)
                    .map(value => ({
                        label: this.$filters.unKebabCase(value),
                        value,
                    }));
            },
            availableChronologies() {
                return Object.values(ScheduleModel.Chronologies);
            },
        },
        data() {
            return {
                groupName: `schedule-reminder-${shortid.generate()}`,
            };
        },
        validations() {
            const validations = {
                action: {
                    role: {
                        required,
                        isAvailable(value) {
                            return this.availableRoles.includes(value);
                        },
                    },
                    schedule: {
                        chronology: {
                            required,
                            isAvailable(value) {
                                return this.availableChronologies.includes(value);
                            },
                        },
                        instructions: {},
                    },
                },
            };

            validations.action.schedule.instructions = this.action.schedule.instructions.reduce((memo, instruction, index) => ({
                ...memo,
                [index]: getScheduleValidation(instruction),
            }), {});

            return validations;
        },
        methods: {
            addInstruction() {
                const instruction = new ScheduleInstructionModel({
                    method: ScheduleInstructionModel.Methods.dateInterval,
                    unit: ScheduleInstructionModel.Units.day,
                });
                this.action.schedule.instructions.push(instruction);
            },
            updateSchedule(index, {chronology = null, instruction = {}}) {
                const origin = this.action.schedule.instructions[index].getPayload();
                this.action.schedule.instructions.splice(index, 1, new ScheduleInstructionModel({
                    ...origin,
                    ...instruction,
                }));
                if (chronology !== null) {
                    this.action.schedule = this.action.schedule.cloneSelf({chronology});
                }
            },
            deleteInstruction(index) {
                this.action.schedule.instructions.splice(index, 1);
            },
            updateInstruction(index, instruction) {
                this.updateSchedule(index, {instruction});
            },
            updateChronology(index, chronology) {
                this.updateSchedule(index, {chronology});
            },
        },
        beforeMount() {
            if (this.action.schedule.instructions.length === 0) {
                this.addInstruction();
            }
        },
    };
</script>
